// ============================================================
// Platform Control · Hosting & DNS — provider health + a calm,
// honest pointer into the per-project hosting surfaces that
// actually own domains and deployments today.
//
// Provider probes are real (usePlatformProviderStatus hits
// /api/health/* in live mode). Domains and deployments live in
// sq_domains / sq_site_deployments and are managed per-project
// at /square/projects/:id/hosting via /api/hosting/*. A global
// cross-project aggregate view is intentionally deferred — the
// per-project lane is the source of truth right now.
// ============================================================

import { useNavigate } from 'react-router-dom'
import { usePlatformProviderStatus, type ProviderState } from '../../../hooks/usePlatformProviderStatus'
import ViewShell, { Card, Section, ActionButton, ActionRow } from './_ViewShell'
import EmptyState from '../../../components/EmptyState'
import { SHELL_TOKENS } from '../../../shell/styles'

function stateColor(s: ProviderState): string {
  switch (s) {
    case 'healthy':  return SHELL_TOKENS.green
    case 'degraded': return SHELL_TOKENS.amber
    case 'down':     return SHELL_TOKENS.red
    default:         return SHELL_TOKENS.inkFaint
  }
}

export default function InfrastructureView() {
  const { probes, loading } = usePlatformProviderStatus()
  const navigate = useNavigate()

  return (
    <ViewShell
      kicker="PLATFORM CONTROL · HOSTING & DNS"
      title="Hosting, Domains & Deployments"
      description="Provider health is global. Domains and deployments live per-project under /square/projects/:id/hosting and are backed by sq_domains and sq_site_deployments via /api/hosting/*."
    >
      <Section label="PROVIDER HEALTH">
        <Card>
          {probes.map((p, i) => (
            <div
              key={p.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0',
                borderBottom:
                  i === probes.length - 1
                    ? 'none'
                    : `1px solid ${SHELL_TOKENS.border}`,
              }}
            >
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: SHELL_TOKENS.ink }}>
                  {p.label}
                </div>
                {p.detail && (
                  <div
                    style={{
                      fontSize: 10,
                      color: SHELL_TOKENS.inkMuted,
                      fontFamily: 'var(--font-mono)',
                      marginTop: 2,
                    }}
                  >
                    {p.detail}
                  </div>
                )}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: stateColor(p.state),
                  fontFamily: 'var(--font-mono)',
                  letterSpacing: '0.10em',
                  textTransform: 'uppercase',
                }}
              >
                {loading ? 'checking…' : p.state}
              </div>
            </div>
          ))}
        </Card>
      </Section>

      <Section label="DOMAINS">
        <Card>
          <div style={{ fontSize: 12, color: SHELL_TOKENS.ink, marginBottom: 6, fontWeight: 700 }}>
            Domains are managed per project
          </div>
          <div style={{ fontSize: 11, color: SHELL_TOKENS.inkMuted, lineHeight: 1.55 }}>
            Custom hostnames live in <code style={{ fontFamily: 'var(--font-mono)' }}>sq_domains</code> and are
            added, verified, and removed from <code style={{ fontFamily: 'var(--font-mono)' }}>/square/projects/:id/hosting</code> via{' '}
            <code style={{ fontFamily: 'var(--font-mono)' }}>/api/hosting/domains/*</code>. Open a project to manage its domains.
          </div>
          <button
            type="button"
            onClick={() => navigate('/square/projects')}
            style={{
              marginTop: 12,
              padding: '7px 12px',
              borderRadius: 8,
              border: `1px solid ${SHELL_TOKENS.border}`,
              background: 'transparent',
              color: SHELL_TOKENS.ink,
              fontSize: 11,
              cursor: 'pointer',
              fontFamily: 'var(--font-mono)',
            }}
          >
            Open Projects
          </button>
        </Card>
      </Section>

      <Section label="DEPLOYMENTS">
        <Card>
          <div style={{ fontSize: 12, color: SHELL_TOKENS.ink, marginBottom: 6, fontWeight: 700 }}>
            Deployments are per project
          </div>
          <div style={{ fontSize: 11, color: SHELL_TOKENS.inkMuted, lineHeight: 1.55 }}>
            Site publishes write real rows to <code style={{ fontFamily: 'var(--font-mono)' }}>sq_site_deployments</code>{' '}
            and operator-triggered hosting deployments write to <code style={{ fontFamily: 'var(--font-mono)' }}>sq_deployments</code>,
            both via <code style={{ fontFamily: 'var(--font-mono)' }}>/api/hosting/deployments/*</code>. Each project's
            hosting tab shows its own deployment history and exposes trigger + verify-domain controls.
          </div>
        </Card>
      </Section>

      <Section label="OPERATOR ACTIONS">
        <ActionRow>
          <ActionButton variant="primary" onClick={() => window.location.reload()}>
            Refresh Health Probes
          </ActionButton>
          <ActionButton onClick={() => navigate('/square/projects')}>
            Open Projects
          </ActionButton>
          <ActionButton onClick={() => navigate('/square/control/database')}>
            Verify Database
          </ActionButton>
          <ActionButton onClick={() => navigate('/square/control/storage')}>
            Verify Storage
          </ActionButton>
          <ActionButton onClick={() => navigate('/square/__diag?debug=1')}>
            Sentry Diagnostics
          </ActionButton>
        </ActionRow>
      </Section>
    </ViewShell>
  )
}
